import { AfterViewChecked, ChangeDetectionStrategy, Component, ElementRef, inject, input, signal, viewChild } from '@angular/core';

import { ChatMessageModel } from '../../models/chat-message.model';
import { OptionalType } from '../../models/types/optional.type';
import { GeneralMeetingsResource } from '../../services/resources/general-meetings.resource';
import { TranslationService } from '../../services/translation.service';

@Component({
    selector: 'sl-chat-conversation',
    templateUrl: './chat-conversation.component.html',
    styleUrls: ['./chat-conversation.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ChatConversationComponent implements AfterViewChecked {
    messageTxt = viewChild.required('messageTxt', { read: ElementRef });
    scrollContainer = viewChild.required('scrollContainer', { read: ElementRef });
    messages = input<ChatMessageModel[]>([]);
    shareholderId = input<string>();

    message = '';
    currentLocaleCode = 'de';
    readonly maxMessageLength = 315;
    readonly isSending = signal(false);

    private messagesCount = 0;
    private readonly generalMeetingsResource = inject(GeneralMeetingsResource);
    private readonly translationService = inject(TranslationService);

    constructor() {
        this.currentLocaleCode = this.translationService.getCurrent().code;
    }

    ngAfterViewChecked(): void {
        if (this.messagesCount !== this.messages().length) {
            this.scrollToBottom();
            this.messagesCount = this.messages().length;
        }
    }

    sendMessage(event: OptionalType<KeyboardEvent> = undefined): void {
        // shift or alt for newline
        if (event?.shiftKey || event?.altKey) {
            return;
        }

        // prevent enter key from adding line break
        event?.preventDefault();

        // additional check for message length
        if (this.message.length > this.maxMessageLength || this.message.length === 0) {
            return;
        }

        this.isSending.set(true);

        // add message so user sees it immediately
        this.messages().push(
            new ChatMessageModel({
                sendTime: new Date(),
                isFromShareholder: !this.shareholderId(),
                message: this.message
            })
        );

        // send message
        this.generalMeetingsResource.sendChatMessage(this.message, this.shareholderId()).subscribe(() => this.isSending.set(false));

        // empty input field immediately, so user doesn't send same message again while it's sending
        this.message = '';
        this.messageTxt().nativeElement.value = '';
    }

    setMessage(text: string): void {
        this.message = text;
    }

    scrollToBottom(): void {
        try {
            this.scrollContainer().nativeElement.scrollTop = this.scrollContainer().nativeElement.scrollHeight;
        } catch {}
    }
}
